import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "goober";
import { mobile, touchOnly } from "clutch/src/Style/style.mjs";

import { readState } from "@/__main__/app-state.mjs";
import GameTile from "@/app/AppShellGameTile.jsx";
import MobileDropdown from "@/app/AppShellMobileDropdown.jsx";
import { GAME_BOX_ICONS, GAMES_TO_DISPLAY } from "@/app/constants.mjs";
import type { GameSymbol } from "@/app/games.mjs";
import { SymbolGame } from "@/app/symbol-game.mjs";
import { getLastPlayedGames } from "@/app/util.mjs";
import AllGamesIcon from "@/inline-assets/all-games.svg";
import DropdownIcon from "@/inline-assets/dropdown.svg";
import NavMoreIcon from "@/inline-assets/nav-more.svg";
import { classNames } from "@/util/class-names.mjs";
import { useCurrentGame } from "@/util/game-route.mjs";
import gameShortNameReassign from "@/util/game-short-name-reassign.mjs";
import useDropdown from "@/util/use-dropdown.mjs";
import { sendInteractionEvent } from "@/util/use-interaction-event.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

const Dropdown = styled("div")`
  display: flex;
  padding: var(--sp-1);
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  &::-webkit-scrollbar {
    width: var(--sp-1);
  }

  &::-webkit-scrollbar-thumb {
    border-radius: var(--br-lg);
    background: var(--shade5-75);
  }

  &::-webkit-scrollbar-track {
    border-radius: var(--br-lg);
  }

  .card {
    border-radius: var(--br) !important;
  }

  div.menu-category {
    display: flex;
    padding: var(--sp-3) var(--sp-2) var(--sp-1) var(--sp-2);
    align-items: center;
    gap: var(--sp-2);
    align-self: stretch;

    span {
      color: var(--shade0);
      font-family: Inter;
      font-size: 0.6875rem;
      font-style: normal;
      font-weight: 725;
      line-height: var(--sp-3); /* 109.091% */
      letter-spacing: 0.02406rem;
      text-transform: uppercase;
    }
  }
`;

const GamesGridWrapper = styled("div")`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  padding: 0 var(--sp-2);
  align-items: flex-start;
  align-content: flex-start;
  gap: var(--sp-2);
  align-self: stretch;

  .card {
    width: 100%;
  }

  .title-wrapper {
    flex-direction: column;
    align-items: flex-start !important;
  }

  ${mobile} {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    padding: var(--sp-5) var(--sp-6);
    align-items: flex-start;
    align-content: flex-start;
    gap: var(--sp-3);
    align-self: stretch;

    .card {
      height: auto;
    }
  }
`;

const GamesGrid = ({ toggleDropdown, gamesToDisplay = GAMES_TO_DISPLAY }) => {
  return (
    <GamesGridWrapper>
      {gamesToDisplay.map((gameSymbol, key) => (
        <GameTile
          key={key}
          gameSymbol={gameSymbol}
          textClassName="type-caption--semi shade1"
          onClick={() => {
            toggleDropdown();
            sendInteractionEvent("app-navigation-games-dropdown-game-tile");
          }}
        />
      ))}
    </GamesGridWrapper>
  );
};

export default function GamesDropdown({
  toggleDropdown,
  showUserGames = false,
}) {
  const { t } = useTranslation();

  const {
    settings: { lastPlayedGames },
  } = useSnapshot(readState);

  const userGames = useMemo(() => {
    return Object.values(
      getLastPlayedGames(lastPlayedGames).reduce(
        (acc, _shortName) => {
          const shortName = gameShortNameReassign(_shortName);
          if (acc[shortName]) return acc;
          acc[shortName] = SymbolGame(shortName);
          return acc;
        },
        {} as Record<string, GameSymbol>,
      ),
    );
  }, [lastPlayedGames]);

  const remainingGames = useMemo(() => {
    return GAMES_TO_DISPLAY.filter(
      (gameSymbol) => !userGames.includes(gameSymbol),
    );
  }, [userGames]);

  return (
    <Dropdown className="games-dropdown">
      {showUserGames && userGames.length ? (
        <>
          <div className="flex column">
            <div className="menu-category">
              <span>{t("common:yourGames", "Your Games")}</span>
            </div>
            <GamesGrid
              toggleDropdown={toggleDropdown}
              gamesToDisplay={userGames}
            />
          </div>
          <div className="divider">
            <div />
          </div>
        </>
      ) : null}
      <div className="flex column">
        <div className="menu-category">
          <span>{t("common:allGames", "All Games")}</span>
        </div>
        <GamesGrid
          toggleDropdown={toggleDropdown}
          gamesToDisplay={remainingGames}
        />
      </div>
    </Dropdown>
  );
}

const GamesWrapper = styled("div", React.forwardRef)`
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--sp-1);
  height: var(--secondary-nav-height);

  svg {
    width: var(--sp-5);
    height: var(--sp-5);
    border-radius: var(--br);
  }

  .all-games-icon {
    width: var(--sp-5);
    height: var(--sp-5);
    color: var(--shade0-25);

    path {
      fill: var(--shade0-25);
    }
  }

  .game-icon {
    background: var(--game-accent-color);

    path {
      fill: var(--shade9);
    }
  }

  .click-area {
    display: flex;
    align-items: center;
    height: 100%;
    cursor: pointer;

    &:hover {
      svg:not(.all-games-icon, .game-icon) {
        background: var(--shade0-15);

        path {
          fill: var(--shade0-75);
        }
      }
      .all-games-icon {
        color: var(--shade0-75);
      }
    }
  }

  .dropdown-icon {
    color: var(--shade1);
  }

  .games-dropdown {
    position: absolute;
    top: calc(100% - var(--sp-1));
    left: calc(var(--sp-1) * -1);

    width: calc(var(--sp-20) * 7);
    max-height: calc(var(--sp-20) * 7);
    border-radius: var(--br-lg);
    border: 1px solid var(--shade3-15);
    background: var(--shade7);
    box-shadow:
      0px 12px 24px 0px rgba(7, 8, 12, 0.25),
      0px 4px 12px 0px rgba(7, 8, 12, 0.25);
    z-index: 4;

    display: flex;
    padding: var(--sp-1);
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    overflow-y: scroll;

    > div.column {
      gap: var(--sp-1);
    }

    > div.divider {
      display: flex;
      padding: var(--sp-0_5) 0 var(--sp-0_5) var(--sp-3);
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: var(--sp-2);
      align-self: stretch;

      > div {
        height: var(--sp-px);
        align-self: stretch;
        background: var(--shade3-15);
      }
    }
  }

  ${touchOnly} {
    display: none;
  }
`;

export function GamesDropdownWithWrapper() {
  const gameSymbol = useCurrentGame();

  const Icon = GAME_BOX_ICONS[gameSymbol];
  const { toggleDropdown, dropdown, ref } = useDropdown();

  return (
    <GamesWrapper className="games-dropdown-item" ref={ref}>
      <div
        className="click-area"
        onClick={() => {
          toggleDropdown();
          sendInteractionEvent("app-navigation-games-dropdown");
        }}
      >
        {Icon ? (
          <Icon className="game-icon" width="24" height="24" />
        ) : (
          <AllGamesIcon className="all-games-icon" />
        )}
        <DropdownIcon className="dropdown-icon" />
      </div>
      {dropdown && (
        <GamesDropdown showUserGames toggleDropdown={toggleDropdown} />
      )}
    </GamesWrapper>
  );
}

const MoreGamesDropdownWrapper = styled("div", React.forwardRef)`
  position: relative;
  display: flex;
  height: var(--sp-9);
  padding: var(--sp-2) var(--sp-3);
  align-items: center;
  gap: var(--sp-2);
  background: var(--shade9);
  box-shadow:
    -8px 0px 8px 0px rgba(14, 16, 21, 0.5),
    -1px 0px 0px 0px #181b21;
  color: var(--shade3);

  > svg {
    cursor: pointer;
  }

  &.active,
  &:hover {
    background: var(--shade8);
    box-shadow:
      -1px 0px 0px 0px rgba(39, 42, 48, 0.5),
      0px 1px 0px 0px var(--shade8);
    color: var(--shade1);
  }

  > .games-dropdown {
    cursor: auto;
    position: absolute;
    right: var(--sp-2);
    top: calc(100% + var(--sp-2));
    z-index: 3;
    display: flex;
    width: 32.375rem;
    height: 36.25rem;
    overflow-y: scroll;
    border-radius: var(--sp-2);
    border: 1px solid var(--shade3-15);
    background: var(--shade7);
    box-shadow:
      0px 12px 24px 0px rgba(7, 8, 12, 0.25),
      0px 4px 12px 0px rgba(7, 8, 12, 0.25);

    ${mobile} {
      display: none;
    }
  }
`;

export function MoreGamesDropdown() {
  const { toggleDropdown, dropdown, ref } = useDropdown();

  return (
    <MoreGamesDropdownWrapper
      ref={ref}
      onClick={toggleDropdown}
      {...classNames(dropdown && "active")}
    >
      <NavMoreIcon />

      {dropdown && (
        <>
          <GamesDropdown toggleDropdown={toggleDropdown} />
          <MobileDropdown title={["common:allGames", "All Games"]}>
            <GamesGrid toggleDropdown={toggleDropdown} />
          </MobileDropdown>
        </>
      )}
    </MoreGamesDropdownWrapper>
  );
}
